import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Gift, Copy, Check, Users, Crown, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralProgram = () => {
  const [referralCode, setReferralCode] = useState(null);
  const [referralCount, setReferralCount] = useState(0);
  const [redeemCode, setRedeemCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchCode = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/referral/code`, { withCredentials: true });
      setReferralCode(res.data.code);
      setReferralCount(res.data.referral_count || 0);
    } catch {
      // Will be created on first fetch
    }
  }, []);

  useEffect(() => {
    fetchCode();
  }, [fetchCode]);

  const copyCode = async () => {
    if (!referralCode) return;
    const shareUrl = `${window.location.origin}?ref=${referralCode}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — try manually');
    }
  };

  const shareReferral = async () => {
    const shareUrl = `${window.location.origin}?ref=${referralCode}`;
    const shareText = `I've been tracking my skin health with InnerGlow and it's amazing! Use my code ${referralCode} to get 7 days of Premium free: ${shareUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'InnerGlow Referral', text: shareText, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      copyCode();
    }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/referral/redeem`, { code: redeemCode.trim() }, { withCredentials: true });
      toast.success(res.data.message);
      setRedeemCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="card-elevated border-0" data-testid="referral-program">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#1A1514]">
          <Gift className="w-5 h-5 text-[#A388EE]" /> Invite Friends, Get Premium
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* How it works */}
        <div className="bg-gradient-to-r from-[#FF6B4A]/5 to-[#4ADE80]/10 rounded-xl p-4">
          <p className="text-sm font-semibold text-[#1A1514] mb-2">How it works</p>
          <div className="space-y-2">
            {[
              { icon: <Share2 className="w-4 h-4" />, text: "Share your referral link with friends" },
              { icon: <Users className="w-4 h-4" />, text: "They sign up and enter your code" },
              { icon: <Crown className="w-4 h-4" />, text: "Both of you get 7 days of Premium free" },
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-[#FF6B4A]/10 flex items-center justify-center text-[#FF6B4A] flex-shrink-0">{step.icon}</div>
                <p className="text-sm text-[#1A1514]">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Your code */}
        <div>
          <p className="text-xs text-[#8C7A76] uppercase tracking-wider mb-2">Your referral code</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-[#FFF2EC] rounded-xl px-4 py-3 flex items-center justify-between border border-[#E5D0C9]">
              <span className="font-mono font-bold text-lg text-[#FF6B4A] tracking-widest" data-testid="referral-code">
                {referralCode || '...'}
              </span>
              <button onClick={copyCode} className="text-[#8C7A76] hover:text-[#FF6B4A] transition-colors" data-testid="copy-referral">
                {copied ? <Check className="w-5 h-5 text-[#4ADE80]" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <Button onClick={shareReferral} className="bg-[#FF6B4A] hover:bg-[#E8552F] rounded-xl h-12 px-4" data-testid="share-referral">
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
          {referralCount > 0 && (
            <p className="text-xs text-[#FF6B4A] mt-2 font-medium">
              {referralCount} friend{referralCount !== 1 ? 's' : ''} joined using your code!
            </p>
          )}
        </div>

        {/* Redeem a code */}
        <div>
          <p className="text-xs text-[#8C7A76] uppercase tracking-wider mb-2">Have a code? Redeem it</p>
          <div className="flex items-center gap-2">
            <Input
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              placeholder="Enter referral code"
              className="flex-1 border-[#E5D0C9] font-mono uppercase tracking-wider"
              data-testid="redeem-input"
            />
            <Button onClick={handleRedeem} disabled={!redeemCode.trim() || loading}
              className="bg-[#A388EE] hover:bg-[#b5664a] rounded-xl px-4" data-testid="redeem-btn">
              Redeem
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralProgram;
